/**
 * Ask Human Form Component
 * Form for drafting and submitting discussion posts to communities
 * Follows the pattern from the original PostDiscussionForm
 */

import '../../../../styles/oc-utilities.css';

import { Multiselect } from '@hl/ui-components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Editor from 'react-simple-wysiwyg';

import { OcChatConfig } from '../../../../types/oc';
import { getCommunitiesCanContribute, submitDiscussionPost } from '../../services/ocEndpoints';
import {
  DiscussionPostDraft,
  OcCommunity,
  TopicTag,
  TopicTagGroup,
} from '../../types/apiTypes';
import {
  ErrorText,
  FieldError,
  Form,
  FormField,
  Input,
  Label,
  LoadingText,
  RequiredIndicator,
  Select,
} from './AskHumanForm.styled';

export type AskHumanFormProps = {
  config: OcChatConfig;
  draft: DiscussionPostDraft;
  onSubmit: (url: string) => Promise<void>;
  onError?: (error: Error) => void;
  formId?: string;
  isSubmitting?: boolean;
  onSubmittingChange?: (submitting: boolean) => void;
};

/**
 * AskHumanForm Component
 * Loads communities, handles form state, validates, and submits
 * Pattern matches original PostDiscussionForm with community loading
 */
const AskHumanForm: React.FC<AskHumanFormProps> = ({
  config,
  draft,
  onSubmit,
  onError,
  formId = 'ask-human-form',
  isSubmitting: externalIsSubmitting,
  onSubmittingChange,
}) => {
  // Form state initialized from draft
  const [selectedCommunityKey, setSelectedCommunityKey] = useState<string>(draft.CommunityKey || '');
  const [subject, setSubject] = useState<string>(draft.Subject || '');
  const [body, setBody] = useState<string>(draft.Message || '');
  const [selectedTopicTagKeys, setSelectedTopicTagKeys] = useState<string[]>(draft.TopicTagKeys || []);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  
  // Communities loading state
  const [communities, setCommunities] = useState<OcCommunity[]>([]);
  const [isLoadingCommunities, setIsLoadingCommunities] = useState(false);
  const [communitiesError, setCommunitiesError] = useState<string | null>(null);
  
  // Submission state - use external state if provided
  const [internalIsSubmitting, setInternalIsSubmitting] = useState(false);
  const isSubmitting = externalIsSubmitting ?? internalIsSubmitting;

  // Load communities on mount
  useEffect(() => {
    const loadCommunities = async () => {
      setIsLoadingCommunities(true);
      setCommunitiesError(null);
      
      try {
        const data = await getCommunitiesCanContribute(config);
        setCommunities(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load communities';
        setCommunitiesError(message);
        // eslint-disable-next-line no-console
        console.error('Failed to load communities:', err);
      } finally {
        setIsLoadingCommunities(false);
      }
    };

    loadCommunities();
  }, [config]);

  // Update form when draft changes
  useEffect(() => {
    if (draft.CommunityKey) {
      setSelectedCommunityKey(draft.CommunityKey);
    }
    if (draft.Subject) {
      setSubject(draft.Subject);
    }
    if (draft.Message) {
      setBody(draft.Message);
    }
    if (draft.TopicTagKeys) {
      setSelectedTopicTagKeys(draft.TopicTagKeys);
    }
  }, [draft]);

  // Get topic tag groups for the selected community
  const selectedCommunity = useMemo(() => {
    if (!selectedCommunityKey || !communities.length) {
      return null;
    }
    return communities.find((c) => c.CommunityKey === selectedCommunityKey) || null;
  }, [selectedCommunityKey, communities]);

  const topicTagGroups = useMemo<TopicTagGroup[]>(() => {
    return selectedCommunity?.TopicTagGroups || [];
  }, [selectedCommunity]);

  const hasTopicTags = topicTagGroups.length > 0 && topicTagGroups.some((g) => g.Tags && g.Tags.length > 0);

  // Convert topic tags to multiselect options
  const topicTagOptions = useMemo(() => {
    const options: Array<{ value: string; display: string }> = [];
    topicTagGroups.forEach((group) => {
      if (group.Tags) {
        group.Tags.forEach((tag: TopicTag) => {
          options.push({
            value: tag.TagKey,
            display: tag.Name,
          });
        });
      }
    });
    return options;
  }, [topicTagGroups]);

  // Handler for topic tag multiselect changes
  const handleTopicTagChange = useCallback(
    (_name: string, value: { dataItems: Array<{ value: string; display: string }> }) => {
      // Multiselect onChange returns { dataItems, metadata }
      const selectedValues = value.dataItems.map((item) => item.value);
      setSelectedTopicTagKeys(selectedValues);
    },
    [],
  );

  // Validation
  const validate = useCallback((): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!selectedCommunityKey) {
      newErrors.community = 'Please select a community';
    }

    if (!subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!body.trim()) {
      newErrors.body = 'Message is required';
    }

    // Validate topic tags if community has them
    if (hasTopicTags && selectedTopicTagKeys.length === 0) {
      newErrors.topicTags = 'At least one topic tag is required for this community';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [selectedCommunityKey, subject, body, hasTopicTags, selectedTopicTagKeys]);

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!validate()) {
        return;
      }

      const updateSubmitting = (value: boolean) => {
        setInternalIsSubmitting(value);
        onSubmittingChange?.(value);
      };

      updateSubmitting(true);
      
      try {
        const response = await submitDiscussionPost(config, {
          communityKey: selectedCommunityKey,
          subject: subject.trim(),
          body: body.trim(),
          topicTagKeys: selectedTopicTagKeys,
        });

        if (response.success && response.discussionUrl) {
          await onSubmit(response.discussionUrl);
        } else {
          throw new Error(response.error || 'Failed to submit post');
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Failed to submit post');
        // eslint-disable-next-line no-console
        console.error('Submission failed:', error);
        if (onError) {
          onError(error);
        }
      } finally {
        updateSubmitting(false);
      }
    },
    [validate, onSubmit, onError, onSubmittingChange, config, selectedCommunityKey, subject, body, selectedTopicTagKeys],
  );

  return (
    <Form id={formId} onSubmit={handleSubmit}>
      {/* Community Selection */}
      <FormField>
        <Label htmlFor="ask-human-community">
          Post to Community <RequiredIndicator>*</RequiredIndicator>
        </Label>
        
        {isLoadingCommunities ? (
          <LoadingText>Loading communities...</LoadingText>
        ) : communitiesError ? (
          <ErrorText>{communitiesError}</ErrorText>
        ) : (
          <>
            <Select
              id="ask-human-community"
              value={selectedCommunityKey}
              onChange={(e) => setSelectedCommunityKey(e.target.value)}
              disabled={isSubmitting}
              $hasError={!!errors.community}
              aria-invalid={!!errors.community}
              aria-describedby={errors.community ? 'community-error' : undefined}
            >
              <option value="">Select a community...</option>
              {communities.map((community) => (
                <option key={community.CommunityKey} value={community.CommunityKey}>
                  {community.CommunityName || community.Name}
                </option>
              ))}
            </Select>
            {errors.community && (
              <FieldError id="community-error">{errors.community}</FieldError>
            )}
          </>
        )}
      </FormField>

      {/* Topic Tags Selection */}
      {hasTopicTags && (
        <FormField>
          <Label htmlFor="ask-human-topic-tags">
            Topic Tags <RequiredIndicator>*</RequiredIndicator>
          </Label>
          <Multiselect
            id="ask-human-topic-tags"
            name="topicTags"
            options={topicTagOptions}
            value={selectedTopicTagKeys.map((key) => topicTagOptions.find((opt) => opt.value === key)).filter(Boolean)}
            onChange={handleTopicTagChange}
            placeholder="Select topic tags..."
            disabled={isSubmitting}
          />
          {errors.topicTags && (
            <FieldError id="topicTags-error">{errors.topicTags}</FieldError>
          )}
        </FormField>
      )}

      {/* Subject Input */}
      <FormField>
        <Label htmlFor="ask-human-subject">
          Discussion Subject <RequiredIndicator>*</RequiredIndicator>
        </Label>
        <Input
          id="ask-human-subject"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={isSubmitting}
          placeholder="Enter a subject for your post"
          $hasError={!!errors.subject}
          aria-invalid={!!errors.subject}
          aria-describedby={errors.subject ? 'subject-error' : undefined}
        />
        {errors.subject && (
          <FieldError id="subject-error">{errors.subject}</FieldError>
        )}
      </FormField>

      {/* Body HTML Editor */}
      <FormField>
        <Label htmlFor="ask-human-body">
          Message <RequiredIndicator>*</RequiredIndicator>
        </Label>
        <Editor
          id="ask-human-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={isSubmitting}
          placeholder="Write your message here"
          aria-invalid={!!errors.body}
          aria-describedby={errors.body ? 'body-error' : undefined}
        />
        {errors.body && (
          <FieldError id="body-error">{errors.body}</FieldError>
        )}
      </FormField>
    </Form>
  );
};

export default React.memo(AskHumanForm);
